import { CPLAT_API_ROUTES, usePost } from '@/api';
import { default as EyeOffIcon } from '@/assets/icons/EyeOffIcon.svg?react';
import { default as EyeOpenIcon } from '@/assets/icons/EyeOpenIcon.svg?react';
import GreenCheckIcon from '@/assets/icons/GreenCheckIcon.svg?react';
import { Button } from '@/components/atoms/NewPcButton';
import Input from '@/components/V3/Input';
import { EmailDomain } from '@/constants/email-domain';
import { ROUTES } from '@/routes';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronRightIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import z from 'zod';

const formSchema = z
  .object({
    email: z.email('유효하지 않은 이메일 형식입니다'),
    password: z
      .string()
      .min(8, { message: '영문, 숫자, 특수문자를 포함한 8~20자리' })
      .max(20, { message: '영문, 숫자, 특수문자를 포함한 8~20자리' })
      .regex(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{};:,<.>]).{8,20}$/, {
        message: '영문, 숫자, 특수문자를 포함한 8~20자리',
      }),
    confirm_password: z.string(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: '비밀번호가 일치하지 않습니다.',
    path: ['confirm_password'],
  });

type FormType = z.infer<typeof formSchema>;

interface Props {
  onCompleted: () => void;
}

export const UpdateAccountForm = ({ onCompleted }: Props) => {
  const [localStorageCplatToken] = useLocalStorage('cplatToken', '');

  const [emailVerified, setEmailVerified] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showSuggestions, setShowSuggestions] = useState(false);

  const {
    register,
    formState: { errors },
    watch,
    setError,
    clearErrors,
    handleSubmit,
    setValue,
  } = useForm<FormType>({ resolver: zodResolver(formSchema) });

  const watchedEmail = watch('email');
  const watchedPassword = watch('password');
  const watchedConfirmPassword = watch('confirm_password');

  const { mutate: onVerifyEmail } = usePost(
    CPLAT_API_ROUTES.verifyEmail,
    undefined,
    undefined,
    true,
  );

  const { mutate: onUpdateAccount } = usePost(
    CPLAT_API_ROUTES.updateAccount,
    undefined,
    undefined,
    true,
  );

  useEffect(() => {
    if (!watchedPassword) return;
    if (watchedPassword === watchedConfirmPassword) setPasswordVerified(true);
  }, [watchedPassword, watchedConfirmPassword]);

  return (
    <div className="flex  w-full max-w-[480px] flex-col items-center gap-8 rounded-[20px] bg-white  text-left">
      <div className="w-full space-y-5">
        <div className="space-y-3">
          <div className="text-gr-800 Title3S18">이메일과 비밀번호 등록이 필요해요</div>
          <div className="text-[#6F6F6F] Body2M16">
            이제 휴대폰 인증 없이 등록한 이메일 계정으로
            <br />
            셀러박스에 로그인할 수 있어요
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex flex-col items-start gap-2">
            <span className="text-gr-800 Caption1M12">이메일</span>
            <div className="flex w-full gap-2">
              <div className="relative w-full">
                <Input
                  placeholder="이메일 주소를 입력해 주세요"
                  {...register('email')}
                  onChange={(e) => {
                    setValue('email', e.target.value);
                  }}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  Error={{
                    isError: !!errors.email?.message,
                    errorMessage: errors.email?.message,
                  }}
                  className="w-full"
                  InputProps={{ endAdornment: emailVerified && <GreenCheckIcon /> }}
                />

                {showSuggestions && watchedEmail && !watchedEmail.includes('@') && (
                  <div className="absolute z-10 mt-1 w-full space-y-2 overflow-hidden rounded-xl border border-gr-200 bg-white p-3 shadow-md">
                    {EmailDomain.map((domain) => (
                      <button
                        key={domain}
                        type="button"
                        onClick={() => {
                          setValue('email', `${watchedEmail}@${domain}`);
                          setShowSuggestions(false);
                        }}
                        className="w-full rounded-sm p-1  text-left hover:bg-gr-100"
                      >
                        <span className="text-gr-800 Body4S14">{watchedEmail}</span>
                        <span className="text-gr-600 Body5M14">@{domain}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {!emailVerified && (
                <Button
                  size="xsmall"
                  disabled={!watchedEmail}
                  onClick={() => {
                    onVerifyEmail(
                      { accountEmail: watchedEmail },
                      {
                        onSuccess: (response) => {
                          const isRegistered = response.data.data[0].isRegistered;
                          if (isRegistered)
                            setError('email', { message: '이미 사용중인 이메일 입니다' });
                          else {
                            setEmailVerified(true);
                            clearErrors();
                          }
                        },
                      },
                    );
                  }}
                >
                  중복확인
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex flex-col items-start gap-2">
              <span className="text-gr-800 Caption1M12">비밀번호</span>
              <Input
                placeholder="영문,숫자,특수문자를 포함한 8~20자리"
                {...register('password')}
                Error={{
                  isError: !!errors.password?.message,
                  errorMessage: errors.password?.message,
                }}
                type={showPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: watchedPassword && (
                    <button onClick={() => setShowPassword((prev) => !prev)}>
                      {showPassword ? (
                        <EyeOpenIcon className="text-gr-400" />
                      ) : (
                        <EyeOffIcon className="text-gr-400" />
                      )}
                    </button>
                  ),
                  endAdornmentStyle: 'top-6 right-2.5',
                }}
              />
            </div>
            <div className="flex flex-col items-start gap-2">
              <Input
                placeholder="비밀번호를 한번 더 입력해 주세요"
                {...register('confirm_password')}
                Error={{
                  isError: !!errors.confirm_password?.message,
                  errorMessage: errors.confirm_password?.message,
                }}
                type={showConfirmPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: passwordVerified ? (
                    <GreenCheckIcon />
                  ) : (
                    watchedConfirmPassword && (
                      <button onClick={() => setShowConfirmPassword((prev) => !prev)}>
                        {showConfirmPassword ? (
                          <EyeOpenIcon className="text-gr-400" />
                        ) : (
                          <EyeOffIcon className="text-gr-400" />
                        )}
                      </button>
                    )
                  ),
                  endAdornmentStyle: 'top-6 right-2.5',
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <Button
        className="w-full"
        disabled={!emailVerified || !passwordVerified}
        onClick={handleSubmit((values) => {
          onUpdateAccount(
            {
              cplatToken: localStorageCplatToken,
              plainPassword: values.password,
              newAccountEmail: values.email,
            },
            {
              onSuccess: () => {
                onCompleted();
              },
            },
          );
        })}
      >
        입력 완료
      </Button>
    </div>
  );
};
